import util from 'preact-util';

const STORAGE_KEY = 'keepspot_pendingSaves';

function getQueue() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch (e) {
        return {};
    }
}

function setQueue(queue) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
}

/**
 * Add a failed save to the offline retry queue.
 * Key is url+id+field so the latest value for a given field always wins (deduplication).
 */
function addToQueue(url, id, field, value) {
    const queue = getQueue();
    const key = `${url}${id}:${field}`;
    queue[key] = { url, id, field, value, ts: Date.now() };
    setQueue(queue);
}

/**
 * Replay all queued saves. Removes successful entries; keeps failures for next retry.
 * @param {object} appState - appState store, used for toast notifications
 * @returns {Promise<number>} count of successful retries
 */
async function retryPendingSaves(appState) {
    const queue = getQueue();
    const keys = Object.keys(queue);
    if (keys.length === 0) {
        return 0;
    }

    let successCount = 0;

    for (const key of keys) {
        const { url, id, field, value } = queue[key];
        try {
            const response = await util.fetchApi(`${url}${id}`, { method: 'PATCH' }, { [field]: value });
            if (response.status < 300) {
                delete queue[key];
                successCount += 1;
            }
        } catch (e) {
            // Keep in queue for next retry
        }
    }

    setQueue(queue);

    if (successCount > 0 && appState && typeof appState.addToast === 'function') {
        appState.addToast({
            type: 'success',
            message: `${successCount} offline ${successCount === 1 ? 'change' : 'changes'} saved successfully.`,
            icon: 'cloud-upload',
        });
    }

    return successCount;
}

/**
 * @returns {number} number of pending saves in the queue
 */
function getQueueSize() {
    return Object.keys(getQueue()).length;
}

/**
 * Clear the entire queue (for debugging/admin).
 */
function clearQueue() {
    localStorage.removeItem(STORAGE_KEY);
}

export default {
    addToQueue,
    retryPendingSaves,
    getQueueSize,
    clearQueue,
};
